import { requireSDK } from '../utils/utils.js';

/**
 * NGX display Auth Facebook object.
 *
 * Summary of responsibilities:
 *
 *  - handles all functionality related to authenticating users against Facebook
 */
export default (function() {
  // Properties.
  var _config = {
      id:    'Facebook',
      ngxId: 'facebook',

      appVersion: '11.0',
    },
    _state = {
      authenticated:    false,
      authResponse:     null,
      identifyResponse: null,
      ready:            false,
    },
    _callbacks = {},
    _event = null;

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Init.
  var init = function(config) {
      // Merge injected params with our own.
      _.extend(_config, config);

      // Convenience shortcuts.
      _event = NGX.App.Events;

      // Init function.
      var fbInit = function() {
        var initData = {
          appId:   _config.appId,
          xfbml:   true,
          version: 'v' + _config.appVersion,
        };
        FB.init(initData);

        // Update ready state.
        _state.ready = true;
      };

      // Just in case it is already defined, backup the existing fbAsyncInit definition.
      var existingFbInit = typeof window.fbAsyncInit !== 'undefined' ? window.fbAsyncInit : function() {};

      // If we are in Facebook.
      if (NGX.context.isFB === true) {
        // SDK will already be loaded, so listen for ready event.
        _event.on('fb:ready', function() {
          // Retain Fb.init() state already called by core setup. This will mean that if logging in via Social Sign-in whilst in a Facebook context
          // the existing config+app id will be used, rather than the value defined for the account whitelabel apps area.

          // Update ready state.
          _state.ready = true;
        });
      } else {
        // Otherwise...
        // Override fbAsyncInit.
        window.fbAsyncInit = function() {
          existingFbInit();
          fbInit();
        };
      }

      // Ensure the FB SDK is available in the event that it isn't already present.
      requireSDK('fb', () => {});
    },
    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
    // Log user in.
    login = function(callbackSuccess, callbackFail, redirectUrl) {
      // Store reference to callbacks.
      _callbacks.login = {};
      _callbacks.login.success = callbackSuccess;
      _callbacks.login.fail = callbackFail;

      // Attempt login. Request additional scope to access users email address.
      FB.login(handleStatusChange, { scope: 'email' });
    },
    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
    // Log user out.
    logout = function() {
      FB.logout(handleStatusChange);
    },
    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
    // When a user logged in/out, handle the change.
    handleStatusChange = function(response) {
      // Store response.
      _state.authResponse = response;

      // If success.
      if (response.status === 'connected') {
        _state.authenticated = true;

        // Build sanitised response object to pass to success callback.
        var authResponse = {};
        authResponse.network = _config.id;
        authResponse.data = response.authResponse;

        // Execute callback.
        _callbacks.login.success(authResponse);
      } else {
        // If fail.
        _state.authenticated = false;

        // Execute callback.
        _callbacks.login.fail();
      }
    },
    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
    // Identify a user (retrieve as many details about them as we can) after being logged in.
    identify = function(user, callbackSuccess, callbackFail) {
      // Store reference to callbacks.
      _callbacks.identify = {};
      _callbacks.identify.success = callbackSuccess;
      _callbacks.identify.fail = callbackFail;

      // Get user id.
      var userId = user.authedNetworks.Facebook.response.userID;

      // Attempt to identify user.
      FB.api('/me?fields=id,first_name,last_name,email', function(response) {
        // Store response.
        _state.identifyResponse = response;

        if (response && !response.error) {
          // Build sanitised response object to pass to success callback.
          var identifyResponse = {};
          identifyResponse.network = _config.id;

          // Attempt to extract user id.
          var id = '';
          try {
            id = userId;
            id = id.toString();
          } catch (exception) {}
          // Attempt to extract name.
          var firstName = '';
          var lastName = '';
          try {
            if (response.hasOwnProperty('first_name')) {
              firstName = response.first_name;
            }
            if (response.hasOwnProperty('last_name')) {
              lastName = response.last_name;
            }
          } catch (exception) {}
          // Attempt to extract email.
          var email = '';
          try {
            if (response.hasOwnProperty('email')) {
              email = response.email;
            }
          } catch (exception) {}
          // Attempt to extract associated data.
          var associatedData = {};
          associatedData.ngxId = _config.ngxId;

          // Profile username.
          associatedData.profileUsername = '';

          // Profile URL.
          associatedData.profileUrl = 'https://facebook.com/' + id;

          // Avatar URL.
          associatedData.avatarUrl =
            'https://graph.facebook.com/v' + _config.appVersion + '/' + id + '/picture?type=normal';

          identifyResponse.data = {
            id:             id,
            firstName:      firstName,
            lastName:       lastName,
            email:          email,
            associatedData: associatedData,
          };

          // Execute callback.
          _callbacks.identify.success(identifyResponse);
        } else {
          // Execute callback.
          _callbacks.identify.fail();
        }
      });
    };

  // Expose public API.
  return {
    init:              init,
    login:             login,
    identify:          identify,
    accountRestricted: true,
  };
})();
